import { useState, useEffect } from "react";
import {
  fetchBankList,
  createWithdrawAccount,
  type BankItem,
} from "@/api/wallet";

interface BindAccountDrawerProps {
  visible: boolean;
  onClose: () => void;
}

export default function BindAccountDrawer({ visible, onClose }: BindAccountDrawerProps) {
  // 表单状态
  const [selectedBank, setSelectedBank] = useState<BankItem | null>(null);
  const [cardNumber, setCardNumber] = useState("");
  const [cardholderName, setCardholderName] = useState("");
  const [reservedPhone, setReservedPhone] = useState("");
  const [showBankPicker, setShowBankPicker] = useState(false);

  // 银行列表
  const [bankList, setBankList] = useState<BankItem[]>([]);
  const [bankLoading, setBankLoading] = useState(false);

  // 提交状态
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible && bankList.length === 0) {
      loadBankList();
    }
  }, [visible]);

  /** 获取银行列表 */
  const loadBankList = async () => {
    try {
      setBankLoading(true);
      const list = await fetchBankList();
      setBankList(list);
    } catch (e) {
      console.error("[BindAccountDrawer] 获取银行列表失败:", e);
    } finally {
      setBankLoading(false);
    }
  };

  /** 提交绑定 */
  const handleSubmit = async () => {
    if (!selectedBank?.name) return alert("请选择开户银行");
    if (!cardNumber || cardNumber.replace(/\D/g, "").length < 10) return alert("请输入正确的银行卡号");
    if (!cardholderName.trim()) return alert("请输入持卡人姓名");
    if (reservedPhone && !/^1\d{10}$/.test(reservedPhone)) return alert("手机号格式不正确");

    try {
      setSubmitting(true);
      await createWithdrawAccount({
        bank_name: selectedBank.name,
        card_number: cardNumber.replace(/\D/g, ""),
        cardholder_name: cardholderName.trim(),
        reserved_phone: reservedPhone || undefined,
        is_default: undefined, // 后端自动处理首张默认
      });
      alert("绑定成功");
      resetForm();
      onClose();
    } catch (e: any) {
      alert(e.message || "绑定失败，请重试");
    } finally {
      setSubmitting(false);
    }
  };

  /** 重置表单 */
  const resetForm = () => {
    setSelectedBank(null);
    setCardNumber("");
    setCardholderName("");
    setReservedPhone("");
  };

  if (!visible) return null;

  return (
    <>
      {/* 遮罩层 */}
      <div
        className="fixed inset-0 bg-black/40 z-[100]"
        onClick={onClose}
      />
      {/* 抽屉内容 */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[101] rounded-t-2xl bg-white animate-slide-up px-5 pt-4 pb-8"
        style={{ maxHeight: "90vh", overflowY: "auto", paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {/* 顶部拖拽条 + 标题栏 */}
        <div className="flex flex-col items-center mb-5">
          <div
            className="w-10 h-1 rounded-full mb-3"
            style={{ background: "#DDDDDD" }}
          />
          <div className="flex items-center justify-between w-full relative">
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center cursor-pointer active:opacity-60"
            >
              <i className="ri-close-line text-xl" style={{ color: "#666666" }} />
            </button>
            <span className="text-[17px] font-semibold absolute left-1/2 -translate-x-1/2" style={{ color: "#333333" }}>
              绑定提现账户
            </span>
            <div className="w-8" />
          </div>
        </div>

        {/* 开户银行 */}
        <div className="mb-4">
          <div className="text-[13px] mb-2" style={{ color: "#666666" }}>开户银行</div>
          <div
            onClick={() => setShowBankPicker(!showBankPicker)}
            className="flex items-center justify-between px-4 py-3.5 rounded-xl cursor-pointer active:bg-gray-50 transition-colors"
            style={{ background: "#F7F7F7", border: "1px solid #EEEEEE" }}
          >
            <div className="flex items-center gap-3">
              <i className="ri-bank-line text-lg" style={{ color: selectedBank ? "#FF7043" : "#BBBBBB" }} />
              <span className="text-sm" style={{ color: selectedBank ? "#333333" : "#CCCCCC" }}>
                {selectedBank ? selectedBank.name : "请选择银行"}
              </span>
            </div>
            <i
              className="ri-arrow-down-s-line transition-transform"
              style={{
                color: "#CCCCCC",
                transform: showBankPicker ? "rotate(180deg)" : "none",
              }}
            />
          </div>

          {/* 银行选择下拉列表 */}
          {showBankPicker && (
            <div
              className="mt-2 rounded-xl max-h-48 overflow-y-auto"
              style={{ background: "#FFFFFF", border: "1px solid #EEEEEE", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
            >
              {bankLoading ? (
                <div className="text-center py-4 text-sm" style={{ color: "#AAAAAA" }}>加载中...</div>
              ) : bankList.map((bank) => (
                <div
                  key={bank.code}
                  onClick={() => {
                    setSelectedBank(bank);
                    setShowBankPicker(false);
                  }}
                  className="px-4 py-3 flex items-center justify-between cursor-pointer active:bg-gray-50 transition-colors border-b last:border-b-0"
                  style={{ borderColor: "#F5F5F5" }}
                >
                  <span className="text-sm" style={{ color: "#333333" }}>{bank.name}</span>
                  {selectedBank?.code === bank.code && (
                    <i className="ri-check-line text-sm" style={{ color: "#FF7043" }} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 银行卡号 */}
        <div className="mb-4">
          <div className="text-[13px] mb-2" style={{ color: "#666666" }}>银行卡号</div>
          <div
            className="flex items-center px-4 py-3.5 rounded-xl"
            style={{ background: "#F7F7F7", border: "1px solid #EEEEEE" }}
          >
            <i className="ri-bank-card-line text-lg mr-3" style={{ color: "#BBBBBB" }} />
            <input
              type="text"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value.replace(/[^\d]/g, "").slice(0, 30))}
              placeholder="请输入银行卡号（10-30位数字）"
              className="flex-1 text-sm outline-none placeholder:text-gray-400"
              style={{ background: "transparent", color: "#333333" }}
            />
          </div>
        </div>

        {/* 持卡人姓名 */}
        <div className="mb-4">
          <div className="text-[13px] mb-2" style={{ color: "#666666" }}>持卡人姓名</div>
          <div
            className="flex items-center px-4 py-3.5 rounded-xl"
            style={{ background: "#F7F7F7", border: "1px solid #EEEEEE" }}
          >
            <i className="ri-user-line text-lg mr-3" style={{ color: "#BBBBBB" }} />
            <input
              type="text"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value.slice(0, 20))}
              placeholder="请输入持卡人真实姓名"
              className="flex-1 text-sm outline-none placeholder:text-gray-400"
              style={{ background: "transparent", color: "#333333" }}
            />
          </div>
        </div>

        {/* 银行预留手机号 */}
        <div className="mb-4">
          <div className="text-[13px] mb-2" style={{ color: "#666666" }}>银行预留手机号</div>
          <div
            className="flex items-center px-4 py-3.5 rounded-xl"
            style={{ background: "#F7F7F7", border: "1px solid #EEEEEE" }}
          >
            <i className="ri-phone-line text-lg mr-3" style={{ color: "#BBBBBB" }} />
            <input
              type="tel"
              value={reservedPhone}
              onChange={(e) => setReservedPhone(e.target.value.replace(/[^\d]/g, "").slice(0, 11))}
              placeholder="请输入银行预留手机号（选填）"
              className="flex-1 text-sm outline-none placeholder:text-gray-400"
              style={{ background: "transparent", color: "#333333" }}
            />
          </div>
        </div>

        {/* 安全提示 */}
        <div
          className="flex items-start gap-2 p-3 rounded-xl mb-5"
          style={{ background: "#FFF9F5", border: "1px solid #FFEDDB" }}
        >
          <i className="ri-shield-star-line text-base mt-0.5" style={{ color: "#FF9966" }} />
          <span className="text-xs leading-relaxed" style={{ color: "#BB8866" }}>
            提现账户信息将通过银行级加密保护，仅用于提现操作，不会以任何理由索取完整卡号
          </span>
        </div>

        {/* 确认绑定按钮 */}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full py-3.5 rounded-full text-white font-medium text-base cursor-pointer active:opacity-80 transition-opacity disabled:opacity-60"
          style={{ background: "linear-gradient(135deg, #FFAB91, #FF7043)" }}
        >
          {submitting ? "提交中..." : "确认绑定"}
        </button>
      </div>
    </>
  );
}
